import { useState, useEffect } from 'react';
import { BookOpen, Award, Volume2 } from 'lucide-react';
import { motion } from 'framer-motion';

const ConceptExplanation = ({ explanation, keyPoints }) => {
  const [visiblePoints, setVisiblePoints] = useState([]);
  const [isExplanationVisible, setIsExplanationVisible] = useState(false);

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      window.speechSynthesis.speak(utterance);
    }
  };

  useEffect(() => {
    // Animate explanation first
    setTimeout(() => setIsExplanationVisible(true), 300);

    // Then animate key points one by one
    keyPoints.forEach((_, index) => {
      setTimeout(() => {
        setVisiblePoints(prev => [...prev, index]);
      }, 800 + index * 200);
    });
  }, [keyPoints]);

  return (
    <div className="card duo-lesson-card" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Duolingo-style decorative elements */}
      <div className="duo-float" style={{
        position: 'absolute',
        top: '-30px',
        right: '-30px',
        width: '80px',
        height: '80px',
        background: 'linear-gradient(135deg, #58cc02, #89e219)',
        borderRadius: '50%',
        opacity: 0.8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2rem',
        boxShadow: '0 6px 0 #46a302, 0 8px 16px rgba(88, 204, 2, 0.3)'
      }}>
        📚
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}
      >
        <div style={{
          background: 'linear-gradient(135deg, #58cc02, #89e219)',
          borderRadius: '20px',
          padding: '16px',
          marginRight: '20px',
          boxShadow: '0 6px 0 #46a302, 0 8px 16px rgba(88, 204, 2, 0.3)'
        }}>
          <BookOpen size={32} color="white" />
        </div>
        <h2 style={{
          color: '#3c3c3c',
          fontSize: '2.4rem',
          fontWeight: '900',
          background: 'linear-gradient(135deg, #58cc02 0%, #89e219 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Lesson Overview
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isExplanationVisible ? 1 : 0, scale: isExplanationVisible ? 1 : 0.95 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        style={{
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          borderRadius: '24px',
          padding: '32px',
          marginBottom: '32px',
          border: '3px solid rgba(88, 204, 2, 0.2)',
          position: 'relative',
          boxShadow: '0 6px 0 rgba(88, 204, 2, 0.1), 0 8px 16px rgba(88, 204, 2, 0.1)'
        }}
      >
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          display: 'flex',
          gap: '10px'
        }}>
          <button 
            onClick={() => speak(explanation)}
            aria-label="Read explanation aloud"
            style={{
              background: 'linear-gradient(135deg, #1cb0f6, #42c2f9)',
              borderRadius: '50%',
              padding: '12px',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 0 #1899d6, 0 6px 12px rgba(28, 176, 246, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.1s'
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(4px)';
              e.currentTarget.style.boxShadow = '0 0px 0 #1899d6, 0 2px 4px rgba(28, 176, 246, 0.3)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 4px 0 #1899d6, 0 6px 12px rgba(28, 176, 246, 0.3)';
            }}
          >
            <Volume2 size={24} color="white" />
          </button>
          
          <div style={{
            background: 'linear-gradient(135deg, #58cc02, #89e219)',
            borderRadius: '50%',
            padding: '12px',
            boxShadow: '0 4px 0 #46a302, 0 6px 12px rgba(88, 204, 2, 0.3)',
            fontSize: '1.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            💡
          </div>
        </div>

        <p style={{
          fontSize: '1.3rem',
          lineHeight: '1.8',
          color: '#166534',
          margin: 0,
          marginTop: '20px',
          fontWeight: '700'
        }}>
          {explanation}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}
      >
        <div style={{
          background: 'linear-gradient(135deg, #ff9500, #ffcd00)',
          borderRadius: '16px',
          padding: '14px',
          marginRight: '16px',
          boxShadow: '0 4px 0 #cc7700, 0 6px 12px rgba(255, 149, 0, 0.3)'
        }}>
          <Award size={28} color="white" />
        </div>
        <h3 style={{
          color: '#3c3c3c',
          fontSize: '1.8rem',
          fontWeight: '900',
          margin: 0
        }}>
          Key Learning Points
        </h3>
      </motion.div>

      <div style={{ display: 'grid', gap: '16px' }}>
        {keyPoints.map((point, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20, scale: 0.95 }}
            animate={{
              opacity: visiblePoints.includes(index) ? 1 : 0,
              x: visiblePoints.includes(index) ? 0 : -20,
              scale: visiblePoints.includes(index) ? 1 : 0.95
            }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{
              background: 'white',
              borderRadius: '20px',
              padding: '24px',
              border: '3px solid #e5e5e5',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '20px',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              boxShadow: '0 4px 0 #e5e5e5, 0 6px 12px rgba(0, 0, 0, 0.1)'
            }}
            whileHover={{
              scale: 1.03,
              boxShadow: '0 6px 0 #e5e5e5, 0 10px 20px rgba(0, 0, 0, 0.15)'
            }}
          >
            <div style={{
              background: visiblePoints.includes(index)
                ? 'linear-gradient(135deg, #58cc02, #89e219)'
                : '#e5e5e5',
              borderRadius: '50%',
              padding: '8px',
              minWidth: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              boxShadow: visiblePoints.includes(index)
                ? '0 4px 0 #46a302, 0 6px 12px rgba(88, 204, 2, 0.3)'
                : '0 2px 0 #d7d7d7',
              fontSize: '1.2rem'
            }}>
              {visiblePoints.includes(index) ? '✅' : '⭕'}
            </div>
            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: '#3c3c3c',
              margin: 0,
              fontWeight: '700',
              flex: 1
            }}>
              {point}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ConceptExplanation;